import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import ModalComponent from '..'
import { AddDeliveryLocation } from '../../../redux-toolkits/products/products.type'
import { AddDeliveryLocationSchema } from '../../../validation/validate'
import { Button } from '../../button'

import { useFetchDeliveryRegionsQuery } from '../../../redux-toolkits/user/user.slice'
import { DeliveryRegionsQuery } from '../../../redux-toolkits/locations/locations.type'
import ComboBox from '../../inputs/combobox'

const deliveryTypeOptions = [
  {
    name: 'Minutes',
    value: 'minutes',
  },
  {
    name: 'Days',
    value: 'days',
  },
]

const deliveryTimeOptions = [
  {
    name: '30 - 45 mins',
    value: {
      name: '30 - 45 mins',
      min: 30,
      max: 45,
      type: deliveryTypeOptions[0].value,
    },
  },
  {
    name: '45 mins - 1 hr',
    value: {
      name: '45 mins - 1 hr',
      min: 45,
      max: 60,
      type: deliveryTypeOptions[0].value,
    },
  },
  {
    name: 'Same day',
    value: {
      name: 'Same day',
      min: 1,
      max: 1,
      type: deliveryTypeOptions[1].value,
    },
  },
  {
    name: '24 hours',
    value: {
      name: '24 hours',
      min: 1,
      max: 1,
      type: deliveryTypeOptions[1].value,
    },
  },
  {
    name: '1 - 2 days',
    value: {
      name: '1 - 2 days',
      min: 1,
      max: 2,
      type: deliveryTypeOptions[1].value,
    },
  },
  {
    name: '3 - 5 days',
    value: {
      name: '3 - 5 days',
      min: 3,
      max: 5,
      type: deliveryTypeOptions[1].value,
    },
  },
  {
    name: '5 - 7 days',
    value: {
      name: '5 - 7 days',
      min: 5,
      max: 7,
      type: deliveryTypeOptions[1].value,
    },
  },
  {
    name: 'Under 15 days',
    value: {
      name: 'Under 15 days',
      min: 1,
      max: 15,
      type: deliveryTypeOptions[1].value,
    },
  },
]

export const AddDeliveryLocationModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: AddDeliveryLocation) => void
  mode: 'edit' | 'create'
  location?: AddDeliveryLocation
  loading?: boolean
  disabledLocationIds?: string[]
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = (val) => {},
  mode,
  location,
  loading,
  disabledLocationIds = [],
}) => {
  const [query, setQuery] = useState<DeliveryRegionsQuery>({
    page: 1,
    limit: 1000,
    search: undefined,
    isAvailable: true,
  })

  const { isFetching, data: locationsData } = useFetchDeliveryRegionsQuery(
    query,
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
    },
  )

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    isValid,
    resetForm,
    setFieldValue,
    initialValues,
  } = useFormik({
    initialValues: location
      ? {
          locationId: location.locationId,
          locationName: location.locationName,
          minimumDeliveryTime: location.minimumDeliveryTime,
          maximumDeliveryTime: location.maximumDeliveryTime,
          type: location.type,
          deliveryTime: location.deliveryTime,
          locationType: location.locationType ?? 'region',
        }
      : {
          locationId: '',
          locationName: '',
          minimumDeliveryTime: 0,
          maximumDeliveryTime: 0,
          type: 'minutes',
          deliveryTime: '',
          locationType: 'region',
        },
    enableReinitialize: true,
    validationSchema: AddDeliveryLocationSchema,
    onSubmit: (values, { resetForm }) => {
      const itemToAdd: AddDeliveryLocation = {
        locationId: values.locationId,
        locationName: values.locationName,
        // minimumDeliveryTime: values.minimumDeliveryTime,
        // maximumDeliveryTime: values.maximumDeliveryTime,
        // type: values.type as DeliveryTimeType,
        deliveryTime: values.deliveryTime,
        locationType: 'region',
      }

      onSuccessCallback(itemToAdd)
    },
  })

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={mode === 'create' ? 'Add new location' : 'Edit location'}
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-4">
        <ComboBox
          type="text"
          name="locationName"
          placeholder="Delivers to"
          value={values.locationName}
          onChange={(e) => {
            handleChange(e)
            setFieldValue('locationId', '')
          }}
          onBlur={handleBlur}
          hasIcon={false}
          errors={
            errors.locationId && touched.locationName ? errors.locationId : ''
          }
          menuItems={locationsData?.data.docs
            .filter((loc) => {
              const isCurrentLocation = loc._id === location?.locationId
              return (
                !loc.parentRegion &&
                (isCurrentLocation || !disabledLocationIds.includes(loc._id))
              )
            })
            .map((loc) => {
              return {
                name: loc.name,
                value: {
                  name: loc.name,
                  value: loc._id,
                },
              }
            })}
          onSelectOption={(selected) => {
            setFieldValue('locationId', selected.value)
            setFieldValue('locationName', selected.name)
          }}
          onSearch={(val) => {
            setQuery({
              ...query,
              search: val || undefined,
              page: 1,
            })
          }}
          loading={isFetching}
          immediate
          position="bottom start"
          menuClassName="!max-h-[250px] shadow-md !z-[999]"
          className="w-full"
          isDropdown
        />

        <div className="flex flex-col gap-2">
          <p className="text-base font-medium text-black">Delivers in</p>
          <select
            className="h-[56px] w-full rounded-md border border-divider-gray bg-white px-4 text-base text-black outline-none focus:border-black"
            name="deliveryTime"
            value={values.deliveryTime}
            onBlur={handleBlur}
            onChange={(event) => {
              const selectedValue = deliveryTimeOptions.find(
                (option) => option.value.name === event.target.value,
              )?.value

              if (!selectedValue) {
                return
              }

              setFieldValue('minimumDeliveryTime', selectedValue.min)
              setFieldValue('maximumDeliveryTime', selectedValue.max)
              setFieldValue('type', selectedValue.type)
              setFieldValue('deliveryTime', selectedValue.name)
            }}
          >
            <option value="">Select delivery time</option>
            {deliveryTimeOptions.map((option) => (
              <option key={option.value.name} value={option.value.name}>
                {option.name}
              </option>
            ))}
          </select>
          {errors.deliveryTime && touched.deliveryTime ? (
            <p className="text-danger text-sm">{errors.deliveryTime}</p>
          ) : null}
        </div>

        <Button
          label="Proceed"
          type="submit"
          className="mt-10"
          disabled={!isValid}
          loading={loading}
          primary
          onClick={() => {
            handleSubmit()
          }}
        />
      </div>
    </ModalComponent>
  )
}
